import { twMerge } from 'tailwind-merge'
import { ERB_TONE, erbDescription, erbTier } from '../utils/erb'

type ErbBadgeProps = {
    value?: string | null
    /** `overlay` sits on the artwork, `inline` sits in a row of text. */
    variant?: 'overlay' | 'inline'
    className?: string
}

/**
 * An item's Earnings Retention Bonus, as a pill.
 *
 * Deliberately the same object as `CategoryCount` on the collection banners — dark, blurred,
 * hairline border, fully rounded, pinned to the top-right of the artwork — because it answers the
 * same *kind* of question in the same place, and a second pill shape would just read as a second
 * system. The difference is that this one is tinted by how good the number is; the fill stays as
 * dark and as opaque as `CategoryCount`'s so it reads over any artwork (see `ERB_TONE`).
 *
 * Renders nothing when the asset has no ERB trait. Operatives and loot boxes generally do not
 * carry one, so absence is ordinary and an empty "— ERB" would be noise on a third of the store.
 */
export const ErbBadge = ({ value, variant = 'inline', className }: ErbBadgeProps) => {
    if (!value) return null

    const tone = ERB_TONE[erbTier(value)]
    const description = erbDescription(value)

    return (
        <span
            title={description}
            aria-label={description}
            className={twMerge(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-bold leading-none',
                tone.className,
                // `CategoryCount` sizes itself in `cqi` because the banner strip is a
                // `container-inline`; the item grid is not one, so this takes a fixed size rather
                // than silently falling back to the `max()` floor.
                variant === 'overlay'
                    ? 'px-3 py-1 text-2xs tracking-wide backdrop-blur-sm'
                    : 'px-2.5 py-1 text-2xs tracking-wide',
                className
            )}
            style={tone.glow ? { boxShadow: tone.glow } : undefined}
        >
            {/* The `+` is the whole point: this is something you gain, not a property you are being
                charged for. The loot box reveal already words it "+2% earnings bonus". */}
            +{value} <span className='opacity-80'>ERB</span>
        </span>
    )
}
